// Quote calculation constants and logic
export const MIN_CALL_FEE_GBP = 79;
export const ADDON_WORKMANSHIP_SPEC_GBP = 79;
export const ADDON_COVER_LETTER_GBP = 4.99;
export const ADDON_TENDER_RETURN_GBP = 4.99;

export const gbp = (n) => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
};

const roundToPounds = (n) => {
  return Math.round(n / 5) * 5;
};

export function calculateQuote(form) {
  const hourly = 750 / 7;

  const requiresCall =
    form.projectType === 'new_build' ||
    form.projectType === 'commercial_large' ||
    form.gifaM2 >= 800 ||
    (form.mixedUse && form.gifaM2 >= 300) ||
    form.units >= 6;

  const hasMinimumInfo = form.hasScaledDrawings && form.hasPhotos;

  if (requiresCall) {
    return {
      mode: 'call',
      hasMinimumInfo,
      totalNow: MIN_CALL_FEE_GBP,
      breakdown: [
        { label: 'Fact-finding call', amount: MIN_CALL_FEE_GBP },
        { label: 'Schedule of Works fee', amount: 0, note: 'Fixed fee issued after call' }
      ],
      notes: {
        workmanshipImportance:
          'The Workmanship Specification is important because it defines quality and installation standards. It reduces assumptions, supports like for like tendering, and helps minimise disputes and variations during delivery.'
      }
    };
  }

  let baseHours = 3;
  if (form.projectType === 'commercial_to_resi') baseHours = 4.5;
  if (form.projectType === 'flat_conversion') baseHours = 4.0;
  if (form.projectType === 'hmo_conversion') baseHours = 5.0;
  if (form.projectType === 'extension') baseHours = 4.0;
  if (form.projectType === 'loft_conversion') baseHours = 4.0;

  let sizeHours = 0;
  if (form.gifaM2 > 150 && form.gifaM2 <= 300) sizeHours = 1;
  else if (form.gifaM2 > 300 && form.gifaM2 <= 600) sizeHours = 2;
  else if (form.gifaM2 > 600 && form.gifaM2 < 800) sizeHours = 4;

  let unitHours = 0;
  if (form.units >= 2) unitHours += Math.min(form.units - 1, 4) * 0.6;

  let complexityHours = 0;
  if (form.drainageChanges) complexityHours += 1.0;
  if (form.fullStripOut) complexityHours += 1.5;
  if (form.mixedUse) complexityHours += 0.75;

  let infoHours = 0;
  if (form.infoLevel === 'photos_only') infoHours += 1.0;
  if (form.infoLevel === 'scaled_drawings') infoHours += 0.5;
  if (form.infoLevel === 'architect_drawings') infoHours += 0.0;
  if (form.infoLevel === 'full_tender_pack') infoHours -= 0.25;

  const hours = Math.max(2.5, baseHours + sizeHours + unitHours + complexityHours + infoHours);
  const rawBase = hours * hourly;

  let turnaroundMult = 1;
  if (form.turnaround === 'priority') turnaroundMult = 1.25;
  if (form.turnaround === 'express') turnaroundMult = 1.5;

  const rawWithTurnaround = rawBase * turnaroundMult;
  const minimumFee = 350;
  const baseFee = Math.max(minimumFee, roundToPounds(rawWithTurnaround));

  let addons = 0;
  if (form.addWorkmanshipSpec) addons += ADDON_WORKMANSHIP_SPEC_GBP;
  if (form.addCoverLetter) addons += ADDON_COVER_LETTER_GBP;
  if (form.addTenderReturnForm) addons += ADDON_TENDER_RETURN_GBP;

  const total = Math.round((baseFee + addons) * 100) / 100;
  const breakdown = [];

  const baseRoundedNoTurnaround = roundToPounds(rawBase);
  const baseComponent = Math.max(minimumFee, baseRoundedNoTurnaround);
  const turnaroundComponent = Math.max(0, baseFee - baseComponent);

  breakdown.push({ label: 'Base fee (scope and structure)', amount: baseComponent });
  if (turnaroundComponent > 0) breakdown.push({ label: 'Turnaround', amount: turnaroundComponent });

  if (form.addWorkmanshipSpec) {
    breakdown.push({
      label: 'Workmanship specification',
      amount: ADDON_WORKMANSHIP_SPEC_GBP,
      note: 'Defines quality and installation standards to reduce assumptions, disputes, and variations.'
    });
  }
  if (form.addCoverLetter) breakdown.push({ label: 'Professional covering letter', amount: ADDON_COVER_LETTER_GBP });
  if (form.addTenderReturnForm) breakdown.push({ label: 'Tender return form', amount: ADDON_TENDER_RETURN_GBP });

  return {
    mode: 'checkout',
    hasMinimumInfo,
    totalNow: total,
    breakdown,
    notes: {
      workmanshipImportance:
        'The Workmanship Specification is important because it defines quality and installation standards. It reduces assumptions, supports like for like tendering, and helps minimise disputes and variations during delivery.'
    }
  };
}

// Valid discount codes
export const DISCOUNT_CODES = {
  'SCOPEOFNOCHARGE': 100,
  '99PROBLEMS': 99.9
};
